import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WifiOff } from "lucide-react";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import {
  addOfflineAction,
  endOfflineSession,
  readOfflineSession,
  syncOfflineSession,
  updateOfflineCatchWeight,
  useOnlineStatus,
  type OfflineAction,
  type OfflineSession,
} from "../lib/offline-session";
import { formatDateTime } from "../lib/format";

function timeLabel(value: string) {
  return new Date(value).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function eventLabel(action: OfflineAction) {
  if (action.kind === "catch")
    return `${action.catch_species === "catfish" ? "Catfish" : "Carp"} caught`;
  if (action.kind === "setup") return "Setup changed";
  switch (action.event_type) {
    case "lost_fish": return "Fish lost";
    case "aborted_take": return "Aborted take";
    case "recast": return "Recast";
    case "rod_moved": return "Rod moved";
    case "liner": return "Liner";
    default: return "Indication";
  }
}

function hasWeight(action: OfflineAction) {
  return action.kind === "catch" &&
    ((action.weight_lb ?? 0) > 0 || (action.weight_oz ?? 0) > 0);
}

export function OfflineSessionDetailsPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const [session, setSession] = useState<OfflineSession | null>(() => readOfflineSession(id));
  const [panel, setPanel] = useState<
    | { type: "catch"; rod: number }
    | { type: "move"; rod: number }
    | { type: "setup"; rod: number }
    | null
  >(null);
  const [catchSpecies, setCatchSpecies] = useState<"carp" | "catfish">("carp");
  const [weightLb, setWeightLb] = useState("");
  const [weightOz, setWeightOz] = useState("");
  const [moveSpot, setMoveSpot] = useState("");
  const [moveDistance, setMoveDistance] = useState("");
  const [moveDepth, setMoveDepth] = useState("");
  const [rig, setRig] = useState("");
  const [feed, setFeed] = useState("");
  const [hookbait, setHookbait] = useState("");
  const [additive, setAdditive] = useState("");
  const [approach, setApproach] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [showEndWarning, setShowEndWarning] = useState(false);
  const [reviewWeights, setReviewWeights] = useState(false);
  const [weightDrafts, setWeightDrafts] = useState<Record<string, { lb: string; oz: string }>>({});

  const refresh = () => setSession(readOfflineSession(id));

  useEffect(() => {
    const changed = () => refresh();
    const synced = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.offlineId === id && detail?.sessionId) {
        navigate(`/sessions/${detail.sessionId}`, { replace: true });
      }
    };
    window.addEventListener("catchcompass:offline-changed", changed);
    window.addEventListener("catchcompass:offline-synced", synced);
    return () => {
      window.removeEventListener("catchcompass:offline-changed", changed);
      window.removeEventListener("catchcompass:offline-synced", synced);
    };
  }, [id, navigate]);

  const rods = useMemo(
    () => Array.from({ length: session?.rods_count ?? 0 }, (_, index) => index + 1),
    [session?.rods_count],
  );

  if (!session) {
    return (
      <div className="mx-auto max-w-[760px] px-4 py-10 text-center">
        <p className="text-sm text-secondary">
          This offline session has already synced or is no longer on this phone.
        </p>
        <Button asChild className="mt-4"><Link to="/sessions">Back to sessions</Link></Button>
      </div>
    );
  }

  const catches = session.actions.filter(
    (action): action is Extract<OfflineAction, { kind: "catch" }> => action.kind === "catch",
  );
  const missingWeights = catches.filter((action) => !hasWeight(action));

  function latestForRod<T extends OfflineAction["kind"]>(
    rod: number,
    kind: T,
  ): Extract<OfflineAction, { kind: T }> | null {
    const matches = session!.actions.filter(
      (action) => action.kind === kind && action.rod_number === rod,
    ) as Extract<OfflineAction, { kind: T }>[];
    return matches.sort(
      (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
    )[0] ?? null;
  }

  function latestPosition(rod: number) {
    const matches = session!.actions.filter(
      (action) => action.kind === "event" && action.event_type === "rod_moved" && action.rod_number === rod,
    ) as Extract<OfflineAction, { kind: "event" }>[];
    return matches.sort(
      (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
    )[0] ?? null;
  }

  const pendingRecast = catches
    .map((catchAction) => {
      const latestRecast = session.actions
        .filter(
          (action) =>
            action.kind === "event" &&
            action.event_type === "recast" &&
            action.rod_number === catchAction.rod_number,
        )
        .sort(
          (a, b) =>
            new Date(b.occurred_at).getTime() -
            new Date(a.occurred_at).getTime(),
        )[0];
      return !latestRecast ||
        new Date(catchAction.occurred_at).getTime() >
          new Date(latestRecast.occurred_at).getTime()
        ? catchAction
        : null;
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        new Date((b as OfflineAction).occurred_at).getTime() -
        new Date((a as OfflineAction).occurred_at).getTime(),
    )[0] as Extract<OfflineAction, { kind: "catch" }> | undefined;

  function quickEvent(
    rod: number,
    eventType: "lost_fish" | "aborted_take" | "knock" | "recast",
  ) {
    addOfflineAction(id, {
      kind: "event",
      rod_number: rod,
      event_type: eventType,
      occurred_at: new Date().toISOString(),
    });
    refresh();
  }

  function saveCatch() {
    if (!panel || panel.type !== "catch") return;
    const anyWeight = weightLb.trim() !== "" || weightOz.trim() !== "";
    addOfflineAction(id, {
      kind: "catch",
      rod_number: panel.rod,
      catch_species: catchSpecies,
      weight_lb: anyWeight ? Number(weightLb || 0) : null,
      weight_oz: anyWeight ? Number(weightOz || 0) : null,
      occurred_at: new Date().toISOString(),
    });
    setWeightLb("");
    setWeightOz("");
    setPanel(null);
    refresh();
  }

  function saveMove() {
    if (!panel || panel.type !== "move" || !moveSpot.trim()) return;
    addOfflineAction(id, {
      kind: "event",
      rod_number: panel.rod,
      event_type: "rod_moved",
      spot_snapshot: moveSpot.trim(),
      distance_m: moveDistance.trim() ? Number(moveDistance) : null,
      depth_ft: moveDepth.trim() ? Number(moveDepth) : null,
      occurred_at: new Date().toISOString(),
    });
    setMoveSpot("");
    setMoveDistance("");
    setMoveDepth("");
    setPanel(null);
    refresh();
  }

  function saveSetup() {
    if (!panel || panel.type !== "setup") return;
    addOfflineAction(id, {
      kind: "setup",
      rod_number: panel.rod,
      rig_snapshot: rig.trim() || null,
      feed_bait_snapshot: feed.trim() || null,
      hookbait_snapshot: hookbait.trim() || null,
      additive_snapshot: additive.trim() || null,
      baiting_approach: approach.trim() || null,
      occurred_at: new Date().toISOString(),
    });
    setRig("");
    setFeed("");
    setHookbait("");
    setAdditive("");
    setApproach("");
    setPanel(null);
    refresh();
  }

  async function syncNow() {
    if (!online) return;
    setSyncing(true);
    setSyncError("");
    try {
      const realId = await syncOfflineSession(id);
      if (realId) navigate(`/sessions/${realId}`, { replace: true });
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : "Could not sync this session yet.");
    } finally {
      setSyncing(false);
    }
  }

  function askToEnd() {
    if (missingWeights.length) {
      setShowEndWarning(true);
      setReviewWeights(false);
      const drafts: Record<string, { lb: string; oz: string }> = {};
      missingWeights.forEach((item) => {
        drafts[item.id] = { lb: "", oz: "" };
      });
      setWeightDrafts(drafts);
      return;
    }
    endOfflineSession(id);
    refresh();
  }

  function saveWeightsAndEnd() {
    for (const item of missingWeights) {
      const draft = weightDrafts[item.id] ?? { lb: "", oz: "" };
      const lb = Number(draft.lb || 0);
      const oz = Number(draft.oz || 0);
      if ((!draft.lb.trim() && !draft.oz.trim()) || (lb === 0 && oz === 0)) return;
      updateOfflineCatchWeight(id, item.id, lb, oz);
    }
    endOfflineSession(id);
    setShowEndWarning(false);
    setReviewWeights(false);
    refresh();
  }

  return (
    <div className="mx-auto max-w-[760px] space-y-5 px-4 py-6 pb-28">
      <header>
        <Link to="/app" className="text-sm font-medium text-accent hover:underline">← My Fishing</Link>
        <div className="mt-2 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-primary">{session.lake_name}</h1>
            <p className="mt-1 text-sm text-secondary">
              {session.peg ? `${session.peg} · ` : ""}Started {formatDateTime(session.start_at)}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-inset px-2.5 py-1 text-xs font-semibold text-secondary">
            <WifiOff size={14} /> Offline
          </span>
        </div>
      </header>

      <section className="rounded-xl border border-accent/40 bg-raised p-4">
        <p className="text-sm font-semibold text-primary">
          {session.end_at ? "Session saved on this phone" : "You can keep fishing normally"}
        </p>
        <p className="mt-1 text-sm leading-6 text-secondary">
          {online
            ? "Signal is back. CatchCompass can now sync this session to your main fishing record."
            : "No signal needed. Actions keep their real bank-side time and will sync when you are connected again."}
        </p>
        {online ? (
          <Button className="mt-3" size="sm" isLoading={syncing} onClick={syncNow}>Sync now</Button>
        ) : null}
        {syncError ? <p className="mt-2 text-sm text-error">{syncError}</p> : null}
      </section>

      <section className="grid grid-cols-3 gap-3">
        <Metric label="Rods" value={session.rods_count} />
        <Metric label="Catches" value={catches.length} />
        <Metric
          label="Target"
          value={session.target_species === "both" ? "Carp & cat" : session.target_species === "catfish" ? "Catfish" : "Carp"}
        />
      </section>

      {!session.end_at && pendingRecast ? (
        <section className="rounded-xl border border-accent/50 bg-inset p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent">
            Catch saved · rod out of water
          </p>
          <p className="mt-1 text-lg font-semibold text-primary">
            Rod {pendingRecast.rod_number} needs a recast
          </p>
          <p className="mt-1 text-sm leading-6 text-secondary">
            When the rod is actually fishing again, record the recast so its
            exposure restarts at the right time. Change the setup or position
            first if you need to.
          </p>
          <Button
            className="mt-3"
            size="sm"
            onClick={() => quickEvent(pendingRecast.rod_number, "recast")}
          >
            Recast same setup
          </Button>
        </section>
      ) : null}

      {!session.end_at ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-primary">Live rods</h2>
          {rods.map((rod) => {
            const setup = latestForRod(rod, "setup");
            const position = latestPosition(rod);
            return (
              <div key={rod} className="rounded-xl border border-border bg-raised p-4">
                <p className="font-semibold text-primary">Rod {rod}</p>
                <p className="mt-1 text-xs leading-5 text-secondary">
                  {[position?.spot_snapshot || "Position not set", setup?.rig_snapshot || "Rig not set", setup?.hookbait_snapshot || "Hookbait not set"].join(" · ")}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setPanel({ type: "catch", rod })}>Caught</Button>
                  <Button size="sm" variant="secondary" onClick={() => quickEvent(rod, "lost_fish")}>Lost</Button>
                  <Button size="sm" variant="secondary" onClick={() => quickEvent(rod, "aborted_take")}>Aborted</Button>
                  <Button size="sm" variant="secondary" onClick={() => quickEvent(rod, "knock")}>Indication</Button>
                  <Button size="sm" variant="secondary" onClick={() => quickEvent(rod, "recast")}>Recast</Button>
                  <Button size="sm" variant="tertiary" onClick={() => setPanel({ type: "move", rod })}>Move</Button>
                  <Button size="sm" variant="tertiary" onClick={() => setPanel({ type: "setup", rod })}>Change setup</Button>
                </div>

                {panel?.rod === rod && panel.type === "catch" ? (
                  <div className="mt-4 space-y-3 rounded-lg bg-inset p-3">
                    <Field label="Species" id={`offline-species-${rod}`}>
                      <select
                        id={`offline-species-${rod}`}
                        className="h-11 w-full rounded-lg border border-border bg-background px-3 text-primary"
                        value={catchSpecies}
                        onChange={(event) => setCatchSpecies(event.target.value as "carp" | "catfish")}
                      >
                        <option value="carp">Carp</option>
                        <option value="catfish">Catfish</option>
                      </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Weight (lb)" id={`offline-lb-${rod}`}>
                        <Input id={`offline-lb-${rod}`} type="number" min="0" max="200" value={weightLb} onChange={(e) => setWeightLb(e.target.value)} placeholder="Optional" />
                      </Field>
                      <Field label="Weight (oz)" id={`offline-oz-${rod}`}>
                        <Input id={`offline-oz-${rod}`} type="number" min="0" max="15" value={weightOz} onChange={(e) => setWeightOz(e.target.value)} placeholder="Optional" />
                      </Field>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveCatch}>Save catch</Button>
                      <Button size="sm" variant="tertiary" onClick={() => setPanel(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : null}

                {panel?.rod === rod && panel.type === "move" ? (
                  <div className="mt-4 space-y-3 rounded-lg bg-inset p-3">
                    <Field label="Where is the rod now?" id={`offline-move-${rod}`}>
                      <Input id={`offline-move-${rod}`} value={moveSpot} onChange={(e) => setMoveSpot(e.target.value)} placeholder="e.g. left margin, island point" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Distance (m)" id={`offline-distance-${rod}`}>
                        <Input id={`offline-distance-${rod}`} type="number" min="0" value={moveDistance} onChange={(e) => setMoveDistance(e.target.value)} placeholder="Optional" />
                      </Field>
                      <Field label="Depth (ft)" id={`offline-depth-${rod}`}>
                        <Input id={`offline-depth-${rod}`} type="number" min="0" value={moveDepth} onChange={(e) => setMoveDepth(e.target.value)} placeholder="Optional" />
                      </Field>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveMove} disabled={!moveSpot.trim()}>Save move</Button>
                      <Button size="sm" variant="tertiary" onClick={() => setPanel(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : null}

                {panel?.rod === rod && panel.type === "setup" ? (
                  <div className="mt-4 space-y-3 rounded-lg bg-inset p-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Rig" id={`offline-rig-${rod}`}><Input id={`offline-rig-${rod}`} value={rig} onChange={(e) => setRig(e.target.value)} placeholder="Optional" /></Field>
                      <Field label="Feed bait" id={`offline-feed-${rod}`}><Input id={`offline-feed-${rod}`} value={feed} onChange={(e) => setFeed(e.target.value)} placeholder="Optional" /></Field>
                      <Field label="Hookbait" id={`offline-hook-${rod}`}><Input id={`offline-hook-${rod}`} value={hookbait} onChange={(e) => setHookbait(e.target.value)} placeholder="Optional" /></Field>
                      <Field label="Attractor" id={`offline-additive-${rod}`}><Input id={`offline-additive-${rod}`} value={additive} onChange={(e) => setAdditive(e.target.value)} placeholder="Optional" /></Field>
                    </div>
                    <Field label="Baiting approach" id={`offline-approach-${rod}`}><Input id={`offline-approach-${rod}`} value={approach} onChange={(e) => setApproach(e.target.value)} placeholder="Optional" /></Field>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveSetup}>Save setup</Button>
                      <Button size="sm" variant="tertiary" onClick={() => setPanel(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-primary">Session activity</h2>
        {session.actions.length ? (
          [...session.actions]
            .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
            .map((action) => (
              <div key={action.id} className="rounded-lg border border-border bg-raised p-3">
                <p className="text-sm font-semibold text-primary">{eventLabel(action)} · Rod {action.rod_number}</p>
                <p className="mt-1 text-xs text-secondary">
                  {timeLabel(action.occurred_at)}
                  {action.kind === "event" && action.spot_snapshot ? ` · ${action.spot_snapshot}` : ""}
                </p>
              </div>
            ))
        ) : (
          <p className="rounded-lg border border-dashed border-border p-5 text-sm text-secondary">Nothing logged yet.</p>
        )}
      </section>

      {!session.end_at ? (
        <section className="rounded-xl border border-border bg-raised p-4">
          {!showEndWarning ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-secondary">Ready to wrap up?</p>
              <Button size="sm" variant="secondary" onClick={askToEnd}>End session</Button>
            </div>
          ) : reviewWeights ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-primary">Add missing weights</p>
              {missingWeights.map((item) => {
                const draft = weightDrafts[item.id] ?? { lb: "", oz: "" };
                return (
                  <div key={item.id} className="rounded-lg bg-inset p-3">
                    <p className="text-sm font-medium text-primary">
                      {timeLabel(item.occurred_at)} · {item.catch_species === "catfish" ? "Catfish" : "Carp"} · Rod {item.rod_number}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Input type="number" min="0" placeholder="lb" value={draft.lb} onChange={(e) => setWeightDrafts((current) => ({ ...current, [item.id]: { ...draft, lb: e.target.value } }))} />
                      <Input type="number" min="0" max="15" placeholder="oz" value={draft.oz} onChange={(e) => setWeightDrafts((current) => ({ ...current, [item.id]: { ...draft, oz: e.target.value } }))} />
                    </div>
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={saveWeightsAndEnd}>Save weights & end session</Button>
                <Button size="sm" variant="tertiary" onClick={() => setReviewWeights(false)}>← Back</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-primary">
                {missingWeights.length === 1 ? "1 catch is missing a weight" : `${missingWeights.length} catches are missing weights`}
              </p>
              <p className="text-sm text-secondary">Add them now, or deliberately finish without them.</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setReviewWeights(true)}>Add weights</Button>
                <Button size="sm" variant="secondary" onClick={() => { endOfflineSession(id); setShowEndWarning(false); refresh(); }}>End session without weights</Button>
                <Button size="sm" variant="tertiary" onClick={() => setShowEndWarning(false)}>Keep fishing</Button>
              </div>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-raised p-3">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 text-sm font-semibold text-primary">{value}</p>
    </div>
  );
}
